/** Fixed rows in `next_step` (editable lines between step 2 and Offer). */
export const HIRING_PROCESS_LINE_1 = "1. AI Resume Screening";
export const HIRING_PROCESS_LINE_2 = "2. AI Phone Interview";
export const HIRING_PROCESS_LABEL_1 = "AI Resume Screening";
export const HIRING_PROCESS_LABEL_2 = "AI Phone Interview";
export const HIRING_PROCESS_LINE_OFFER = "Offer";

/** Placeholder for optional steps (3+) in the hiring process editor. */
export const HIRING_PROCESS_INPUT_PLACEHOLDER = "e.g. In-person interview";

/**
 * Does this line already say "the offer stage"? Legacy rows and judge-suggested rewrites word the
 * offer freely ("Final offer", "3. Offer") while buildHiringProcessNextStep re-appends its own
 * "Offer", so one we fail to recognise reaches the candidate as two offer stages. Capped at 3 words
 * to keep a real stage that merely mentions an offer ("Offer negotiation with the hiring manager").
 */
const isTerminalOfferLine = (line: string): boolean => {
  const words = line
    .split(/\s+/)
    .map((word) => word.replace(/[^a-z]/gi, "").toLowerCase())
    .filter(Boolean);
  return (
    words.length > 0 &&
    words.length <= 3 &&
    words.includes(HIRING_PROCESS_LINE_OFFER.toLowerCase())
  );
};

/** Lines between the fixed header and `Offer` (may be empty — no optional steps). */
export const parseHiringProcessMiddleLines = (
  raw: string | null | undefined,
): string[] => {
  if (!raw?.trim()) return [];
  const lines = raw.split("\n").map((l) => l.trim());
  if (
    lines[0] === HIRING_PROCESS_LINE_1 &&
    lines[1] === HIRING_PROCESS_LINE_2
  ) {
    let rest = lines.slice(2);
    while (rest.length && rest[rest.length - 1] === "") {
      rest = rest.slice(0, -1);
    }
    if (rest.length && rest[rest.length - 1] === HIRING_PROCESS_LINE_OFFER) {
      rest = rest.slice(0, -1);
    }
    return rest.length > 0 ? rest : [];
  }
  // Header-less values word their offer row freely, so it takes a fuzzy match; the canonical branch
  // above compares exactly, because there a near-miss is a step the recruiter typed.
  let nonEmpty = lines.filter((l) => l.length > 0);
  if (nonEmpty.length && isTerminalOfferLine(nonEmpty[nonEmpty.length - 1])) {
    nonEmpty = nonEmpty.slice(0, -1);
  }
  return nonEmpty.length > 0 ? nonEmpty : [];
};

export const buildHiringProcessNextStep = (middleLines: string[]): string => {
  const body = middleLines.map((s) => s.trim()).filter(Boolean);
  return [
    HIRING_PROCESS_LINE_1,
    HIRING_PROCESS_LINE_2,
    ...body,
    HIRING_PROCESS_LINE_OFFER,
  ].join("\n");
};
